"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { usePwa } from "./pwa-provider";

type Me = { id: string; name: string; phone: string; provider: string } | null;

const rowStyle = (clickable = false): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: 13,
  padding: "12px 12px",
  borderRadius: 13,
  color: "var(--ink-dim)",
  background: "transparent",
  border: "1px solid transparent",
  cursor: clickable ? "pointer" : "default",
  font: "inherit",
  width: "100%",
  textDecoration: "none",
});

// ── 앱 설치 버튼 (beforeinstallprompt 가 잡혔을 때만 노출) ──
export function InstallButton({ inRail }: { inRail: boolean }) {
  const { canInstall, isStandalone, install } = usePwa();
  if (isStandalone || !canInstall) return null;
  return (
    <button className="navlink" onClick={install} style={rowStyle(true)} aria-label="앱 설치">
      <svg width={21} height={21} viewBox="0 0 24 24" fill="none" stroke="var(--gold-bright)" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none" }}>
        <path d="M12 3v12m0 0l-4-4m4 4l4-4" />
        <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
      </svg>
      <span className={inRail ? "raillabel" : undefined} style={{ fontSize: 14, color: "var(--gold-bright)", fontWeight: 600 }}>앱 설치</span>
    </button>
  );
}

// ── 로그인 상태 + 로그아웃 ──
export function AuthBox({ inRail }: { inRail: boolean }) {
  const router = useRouter();
  const [me, setMe] = useState<Me | undefined>(undefined); // undefined=로딩

  useEffect(() => {
    fetch("/api/auth/me")
      .then((r) => r.json())
      .then((d) => setMe(d.user ?? null))
      .catch(() => setMe(null));
  }, []);

  if (me === undefined) return null;

  if (!me) {
    return (
      <Link href="/login" className="navlink" style={rowStyle(true)}>
        <svg width={21} height={21} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{ flex: "none" }}>
          <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" /><path d="M10 17l5-5-5-5" /><path d="M15 12H3" strokeLinecap="round" />
        </svg>
        <span className={inRail ? "raillabel" : undefined} style={{ fontSize: 14 }}>로그인</span>
      </Link>
    );
  }

  const initial = (me.name || "유").trim().charAt(0) || "유";
  const logout = async () => {
    await fetch("/api/auth/logout", { method: "POST" }).catch(() => {});
    router.push("/landing");
    router.refresh();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* 프로필 표시 */}
      <div style={{ ...rowStyle(false), gap: 11 }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--btn-grad)", color: "var(--btn-ink)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flex: "none" }}>
          {initial}
        </div>
        <span className={inRail ? "raillabel" : undefined} style={{ fontSize: 13.5, color: "var(--ink)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{me.name}</span>
      </div>
      {/* 로그아웃 */}
      <button className="navlink" onClick={logout} style={rowStyle(true)} aria-label="로그아웃">
        <svg width={21} height={21} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{ flex: "none" }}>
          <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><path d="M14 17l5-5-5-5" /><path d="M19 12H7" strokeLinecap="round" />
        </svg>
        <span className={inRail ? "raillabel" : undefined} style={{ fontSize: 14 }}>로그아웃</span>
      </button>
    </div>
  );
}
